import React, { useState, useEffect, useCallback } from "react";

//routing
import { useParams } from "react-router-dom";

//components
import DataTable from "../../../../components/Tables/DataTable";

//hooks
import useLazyQueryAuth from "../../../../hooks/useLazyQueryAuth";
import useQueryAuth from "../../../../hooks/useQueryAuth";
import useAuth from "../../../../hooks/useAuth";
import useMutationAuth from "../../../../hooks/useMutationAuth";

//graphql
import {
	GET_ALL_CUSTOMERS,
	DELETE_CUSTOMER_LIST,
} from "../../../../graphql/customer";
import { GET_USER_TABLE_PREFERENCES } from "../../../../graphql/userTablePreferences";

//editors
import { Parse } from "../../../../JSON.editor";

//mnemonic values
import Mnemonic from "../../../../Mnemonics.json";
import { formatDateTimeToString } from "../../../../UTC2Local";

//urls
import urls from "../../../../urls";

//external components
import { CircularProgress } from "@mui/material";

import ContainerCard from "../../../../components/Cards/ContainerCard";
import UTC2Local from "../../../../UTC2Local";
import componentDisplay from "../../../../componentDisplay";
import { useSnackbar } from "notistack";

import { useTranslation } from "react-i18next";
import { decodeUnicodeString } from "../../../../utils/decodeUnicodeString";
import { removeNullProps } from "../../../../utils/removeNullProps";
import { GET_ALL_COMPANIES_DROPDOWN } from "../../../../graphql/company";

const CustomerList = ({ fromTenant = false, myCompany = false }) => {
	const { tID, tName } = useParams();

	const { customer_form, my_new_customer } = urls;

	const { storeUserGuid, userRoles, storeUserTenant } = useAuth();

	const { manageParameters } = userRoles;

	const { t } = useTranslation();

	const { enqueueSnackbar } = useSnackbar();

	const [re, setRe] = useState(false);
	const [data, setData] = useState(null);
	const [defaultFilters, setDefaultFilters] = useState({});
	const [columns, setColumns] = useState([]);
	const [order, setOrder] = useState("");
	const [orderBy, setOrderBy] = useState("");
	const [selected, setSelected] = useState([]);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(20);
	const [search, setSearch] = useState("");

	const { mfunction: deleteRecords, loading: deleteLoading } = useMutationAuth(
		DELETE_CUSTOMER_LIST,
		"DeleteCustomersList"
	);
	const {
		data: companiesData,
		loading: companyLoading,
		tokenChange: companyWait,
	} = useQueryAuth(GET_ALL_COMPANIES_DROPDOWN, "GetAllCompanies", {
		variables: {
			model: {
				enableFilters: false,
				userID: storeUserGuid,
				tenantID: storeUserTenant,
			},
		},
	});
	const filtersObject = [
		{
			label: "customer.list.filters.company",
			name: "companyID",
			type: "dropdown",
			options:
				companiesData?.GetAllCompanies?.compList?.companies.map(
					(company) => ({
						label: company.name,
						value: company.companyGuid,
					})
				) || [],
		},
		// {
		// 	label: "customer.list.filters.code",
		// 	name: "code",
		// 	type: "text",
		// },
		// {
		// 	label: "customer.list.filters.name",
		// 	name: "name",
		// 	type: "text",
		// },
	];

	const {
		loading: getColumnsLoading,
		tokenChange: columnsWait,
		refetch,
	} = useQueryAuth(
		GET_USER_TABLE_PREFERENCES,
		"GetUserTablePreferences",
		{
			variables: {
				model: {
					table: Mnemonic.Tables.customers,
					userID: storeUserGuid,
				},
			},
		},
		{
			onCompleted: (response) => {
				let col =
					response &&
					response.GetUserTablePreferences &&
					response.GetUserTablePreferences.columns
						? response.GetUserTablePreferences.columns
						: null;

				if (col) {
					col = decodeUnicodeString(col);

					//we parse the json string to a json object
					const parsed = JSON.parse(col, (key, value) =>
						Parse(
							key,
							value,
							UTC2Local,
							componentDisplay,
							null,
							null,
							null,
							formatDateTimeToString
						)
					);

					if (parsed.columns) {
						let cols = parsed.columns;

						cols.map((c) => {
							c = removeNullProps(c);
							return c;
						});
						setColumns(cols);
					}

					if (parsed.defaultSort) setOrderBy(parsed.defaultSort);

					if (parsed.defaultSortDirection)
						setOrder(parsed.defaultSortDirection);
				}
			},
		}
	);

	const { mfunction: getAllCustomers, loading } = useLazyQueryAuth(
		GET_ALL_CUSTOMERS,
		"GetAllCustomers"
	);

	const fetchTableData = useCallback(
		(
			page = 0,
			pageSize = 0,
			order = "",
			orderBy = "",
			search = "",
			filters = null
		) => {
			getAllCustomers(
				{
					variables: {
						model: {
							enableFilters: true,
							keys: {
								page: page + 1,
								pageSize: pageSize,
							},
							orderColumn: orderBy,
							orderDirection: order,
							keyWord: search,
							// tenantID: tID && fromTenant ? tID : storeUserTenant,
							userID: storeUserGuid,
							filters: filters,
						},
					},
				},
				(response) => {
					setData(response);
				}
			);
		},
		[]
	);

	useEffect(() => {
		if (!columnsWait)
			fetchTableData(
				page,
				rowsPerPage,
				order,
				orderBy,
				search,
				defaultFilters
			);
	}, [columnsWait, fetchTableData, re]);

	const rows =
		data && data.GetAllCustomers && data.GetAllCustomers.customers
			? data.GetAllCustomers.customers
			: [];

	const numberOfRecords =
		data && data.GetAllCustomers && data.GetAllCustomers.totalNumberOfRecords
			? data.GetAllCustomers.totalNumberOfRecords
			: 0;

	const handleDelete = () => {
		deleteRecords(
			{
				variables: {
					model: {
						customerIDs: selected,
						userID: storeUserGuid,
					},
				},
			},
			(response) => {
				enqueueSnackbar(t("customer.list.delete.success"), {
					variant: "success",
				});
				setRe((v) => !v);
				setSelected([]);
				setPage(0);
			}
		);
	};

	const pagination = {
		rows,
		columns,
		order,
		setOrder,
		orderBy,
		setOrderBy,
		selected,
		defaultFilters,
		setDefaultFilters,
		setSelected,
		search,
		setSearch,
		setPage,
		setRowsPerPage,
		numberRecords: numberOfRecords,
		page,
		rowsPerPage,
		fetchTableData,
		loading: loading || columnsWait,
		searchText: "customer.list.search.label",
		refetch,
		removeFilters: false,
		tableMnemonic: Mnemonic.Tables.customers,
		filtersObject,
		generateRoute: (row) =>
			customer_form.replace(
				"/:cName/:cID",
				`/${encodeURIComponent(row?.name)}/${encodeURIComponent(
					row?.customerGuid
				)}`
			),
		newText: "company.list.new.label",
		newLink: my_new_customer,
		deleteText: "company.list.delete.label",
		handleDelete: handleDelete,
		deleteLoading,
		readOnly: manageParameters !== "M",
	};

	return (
		<div>
			{getColumnsLoading ? (
				<ContainerCard
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						padding: "20px",
					}}
				>
					<CircularProgress />
				</ContainerCard>
			) : (
				<DataTable
					{...pagination}
					identifier={"customerGuid"}
					hasCheckbox
				/>
			)}
		</div>
	);
};

export default CustomerList;
